import { useEffect } from 'react';

import { BASE_PATH } from '$env';
import getStore from '$store';
import { useNavigateToDefault } from '~hooks/useNavigateToPage';
import { FontOrderPage } from '~pages/FontOrderPage';
import { ControlActions, LocationActions } from '~store/actions';
import { stopEditingEntity } from '~store/thunks/entity';
import { isLessThanMedium } from '~utils/browser';
import ChangelogPage from './pages/ChangelogPage';
import ContentPage from './pages/ContentPage';
import DownloadRequestPage from './pages/DownloadRequestPage';
import EditComponentsPage from './pages/EditComponentsPage';
import EditNavigationPage from './pages/EditNavigationPage';
import EditUsergroupsPage from './pages/EditUsergroupsPage';
import FavouritesPage from './pages/FavouritesPage';
import ForbiddenPage from './pages/ForbiddenPage';
import IconLibraryPage from './pages/IconLibraryPage';
import MyFilesPage from './pages/MyFilesPage';
import SearchPage from './pages/SearchPage';
import TankLabellingPage from './pages/TankLabellingPage';
import TruckLabelingPage from './pages/TruckLabelingPage';
import Web2PrintPage from './pages/Web2PrintPage';

export const ROUTE_START = `/:lang?/`;
export const ROUTE_DEFAULT = `/:lang?/${BASE_PATH}`;
//export const ROUTE_LOGIN = `/:lang?/${BASE_PATH}/login`;
//export const ROUTE_LOGOUT = '/user/logout';
export const ROUTE_ICON_LIBRARY = `/:lang?/${BASE_PATH}/_/icon-library/:realm?`;
export const ROUTE_CHANGELOG = `/:lang?/${BASE_PATH}/_/newsfeed`;
export const ROUTE_EDIT_COMPONENTS = `/:lang?/${BASE_PATH}/edit/components`;
export const ROUTE_EDIT_NAVIGATION = `/:lang?/${BASE_PATH}/edit/navigation`;
export const ROUTE_EDIT_USERGROUPS = `/:lang?/${BASE_PATH}/edit/usergroups`;
export const ROUTE_FAVOURITES = `/:lang?/${BASE_PATH}/_/favourites`;
export const ROUTE_MYFILES = `/:lang?/${BASE_PATH}/_/myfiles`;
export const ROUTE_FORBIDDEN = `/:lang?/${BASE_PATH}/_/forbidden`;
export const ROUTE_TRUCK_LABELING = `/:lang?/${BASE_PATH}/_/truck-branding/:type?/:action?`;
export const ROUTE_TANK_LABELING = `/:lang?/${BASE_PATH}/_/tank-labelling/`;
export const ROUTE_FONT_ORDER = `/:lang?/${BASE_PATH}/_/font-order/`;
export const ROUTE_WEB2PRINT = `/:lang?/${BASE_PATH}/_/web2print`;
export const ROUTE_SEARCH = `/:lang?/${BASE_PATH}/_/search/:term?`;
export const ROUTE_PAGE = `/:lang?/${BASE_PATH}/:pageAlias`;
export const ROUTE_PAGE_WITH_ANCHOR = `/:lang?/${BASE_PATH}/:pageAlias/:slug`;
export const ROUTE_PAGE_WITH_ASSET = `/:lang?/${BASE_PATH}/:pageAlias/asset/:assetId`;
export const ROUTER_PAGE_WITH_ASSET_REVIEW = `/:lang?/${BASE_PATH}/:pageAlias/asset/:assetId/review`;
export const ROUTE_DOWNLOAD_REQUEST = `/:lang?/${BASE_PATH}/_/download-request/:action/:token`;

const RedirectToDefaultPage = () => {
  const navigateToDefault = useNavigateToDefault();

  useEffect(() => {
    navigateToDefault();
  }, [navigateToDefault]);

  return null;
};

const routes = [
  { path: ROUTE_START, Component: RedirectToDefaultPage },
  { path: ROUTE_DEFAULT, Component: RedirectToDefaultPage },
  { path: ROUTE_ICON_LIBRARY, Component: IconLibraryPage },
  { path: ROUTE_CHANGELOG, Component: ChangelogPage },
  { path: ROUTE_EDIT_COMPONENTS, Component: EditComponentsPage },
  { path: ROUTE_EDIT_NAVIGATION, Component: EditNavigationPage },
  { path: ROUTE_EDIT_USERGROUPS, Component: EditUsergroupsPage },
  { path: ROUTE_FAVOURITES, Component: FavouritesPage },
  { path: ROUTE_MYFILES, Component: MyFilesPage },
  { path: ROUTE_FORBIDDEN, Component: ForbiddenPage },
  { path: ROUTE_TRUCK_LABELING, Component: TruckLabelingPage },
  { path: ROUTE_TANK_LABELING, Component: TankLabellingPage },
  { path: ROUTE_FONT_ORDER, Component: FontOrderPage },
  { path: ROUTE_WEB2PRINT, Component: Web2PrintPage },
  { path: ROUTE_SEARCH, Component: SearchPage },
  {
    path: ROUTE_PAGE,
    Component: ContentPage,
    loader: async params => {
      await onBeforeLoadContentPage(params);
    },
  },
  {
    path: ROUTE_PAGE_WITH_ANCHOR,
    Component: ContentPage,
    loader: async params => {
      await onBeforeLoadContentPage(params);
    },
  },
  {
    path: ROUTE_PAGE_WITH_ASSET,
    Component: ContentPage,
    loader: async params => {
      await onBeforeLoadContentPage(params);
    },
  },
  {
    path: ROUTER_PAGE_WITH_ASSET_REVIEW,
    Component: ContentPage,
    loader: async params => {
      await onBeforeLoadContentPage(params);
    },
  },
  { path: ROUTE_DOWNLOAD_REQUEST, Component: DownloadRequestPage },
];

async function onBeforeLoadContentPage(payload = null) {
  const store = getStore();
  store.dispatch(stopEditingEntity());

  // to use selectors app needs page alias in the store
  await store.dispatch(ControlActions.setActiveAnchor(payload.params.slug ?? ''));
  await store.dispatch(LocationActions.setPageAlias(payload.params.pageAlias ?? ''));

  // in mobile view close sidebar
  if (isLessThanMedium()) {
    store.dispatch(ControlActions.closeSidebar());
  }
}

export function getPathnameByType(type: string, payload: Record<string, string>): string {
  return type.replace(/:([a-zA-Z0-9_]+)\??/g, (match, key) => {
    if (key in payload) {
      return payload[key];
    }
    return match.includes('?') ? '' : match;
  });
}

export default routes;
